import { CSSTransition } from 'react-transition-group';
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { SlidingPanelsContext } from './SlidingPanel.context';
import DialogHeader from '../DialogHeader/DialogHeader';

import * as Styled from './SlidingPanel.styled';

/**
 * Panels that slide in and out of view
 */
const SlidingPanel = ({
  className,
  isRoot,
  children,
  isOpen,
  onClose,
  onPrevious,
  panelId,
  title,
  withClose,
  leftHeaderComponent,
  closeDataLabel,
  backDataLabel,
  ...props
}) => {
  const context = useContext(SlidingPanelsContext);

  /**
   * Handler for "close" icon.
   * Typically, this button causes the current panel to close.
   * It also will call the "onClose" prop for custom handling.
   *
   * @param {SyntheticBaseEvent} e React synthetic event
   */
  const handleClose = (e) => {
    if (onClose) {
      onClose(e);
    }

    if (context) {
      const { closeAll } = context;

      closeAll();
    }
  };

  /**
   * Handler for the "previous" icon.
   * Typically, this button causes the current
   * panel to close to reveal another panel underneath.
   *
   * It will also call the "onPrevious" prop for custom
   * handling
   *
   * @param {SyntenticEvent} e React synthetic event
   */
  const handlePrevious = (e) => {
    if (onPrevious) {
      onPrevious(e);
    }

    if (context && panelId) {
      const { removePanel } = context;

      removePanel(panelId);
    }
  };

  return (
    <CSSTransition
      appear={!isRoot}
      in={isOpen}
      classNames="slide"
      timeout={{ enter: 0, exit: 200 }}
      mountOnEnter
      unmountOnExit
    >
      {(animationState) => (
        <Styled.SlidingPanel
          className={className}
          animationState={animationState}
          {...props}
        >
          <DialogHeader
            leftComponent={
              !isRoot ? (
                <Styled.ButtonIcon
                  name="chevron-left"
                  variant="defaultLight"
                  onClick={handlePrevious}
                  data-label={backDataLabel}
                  data-testid="SlidingPanel__BackButton"
                  aria-label="go to previous slide"
                  animationState={animationState}
                />
              ) : (
                leftHeaderComponent
              )
            }
            title={
              <Styled.Title animationState={animationState}>
                {title}
              </Styled.Title>
            }
            rightComponent={
              withClose && (
                <Styled.CloseIcon
                  name="close"
                  variant="secondary"
                  isGhost
                  onClick={handleClose}
                  data-label={closeDataLabel}
                  data-testid="SlidingPanel__Close"
                  aria-label="close"
                />
              )
            }
          />
          <Styled.ChildrenContainer>{children}</Styled.ChildrenContainer>
        </Styled.SlidingPanel>
      )}
    </CSSTransition>
  );
};

SlidingPanel.propTypes = {
  /**
   * Child components
   */
  children: PropTypes.node,
  /**
   * HTML class name
   */
  className: PropTypes.string,
  /**
   * Whether or not panel is open
   */
  isOpen: PropTypes.bool,
  /**
   * Whether panel should display
   * a "previous" button
   */
  isRoot: PropTypes.bool,
  /**
   * Close icon click handler
   */
  onClose: PropTypes.func,
  /**
   * Previous icon click handler
   */
  onPrevious: PropTypes.func,
  /**
   * Unique identifier for panel.
   *
   * Leave this as REQUIRED. It is used in SlidingPanels.context
   *
   * @see SlidingPanels.context.js
   *
   */
  panelId: PropTypes.string.isRequired,
  /**
   * Text to display at top of panel
   */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /**
   * Whether to show the close icon
   */
  withClose: PropTypes.bool,
  /**
   * If provided and is not at the root level, renders the component on the left side of the header; otherwise, renders a back arrow
   */
  leftHeaderComponent: PropTypes.node,
  /**
   * A data-label attribute for the close button
   */
  closeDataLabel: PropTypes.string,
  /**
   * A data-label attribute for the back button
   */
  backDataLabel: PropTypes.string
};

SlidingPanel.defaultProps = {
  children: null,
  className: null,
  isOpen: false,
  isRoot: false,
  onClose: null,
  onPrevious: null,
  title: null,
  withClose: true,
  leftHeaderComponent: null,
  closeDataLabel: 'modal_close',
  backDataLabel: 'modal_back'
};

export default SlidingPanel;
